import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Guest-tour progress, kept entirely in localStorage — no DB row, nothing submitted to
 * the server while touring. On conversion the stored quiz answers can be replayed
 * through the authed endpoints so a real account inherits the work. Cleared when the
 * guest converts (or on demand).
 */
export interface GuestQuizResult {
  quizId: number;
  score: number;
  total: number;
  /** Did this attempt pass (percentage >= passing score)? Sticky across retakes —
   *  once passed, a later failed retake never re-locks progression (mirrors the
   *  server's `completed = completed || passed`). */
  passed?: boolean;
  /** questionId -> the answer the guest gave (for replay on conversion). */
  answers: Record<number, unknown>;
}

interface GuestState {
  viewedLessons: number[];
  quizResults: Record<number, GuestQuizResult>;
  markLessonViewed: (lessonId: number) => void;
  recordQuiz: (result: GuestQuizResult) => void;
  /** Drop one quiz's record — used by the conversion replay after a successful
   *  server-side submit (per-quiz, so failures stay replayable). */
  removeQuiz: (quizId: number) => void;
  reset: () => void;
}

export const useGuestStore = create<GuestState>()(
  persist(
    (set, get) => ({
      viewedLessons: [],
      quizResults: {},

      markLessonViewed: (lessonId) => {
        if (get().viewedLessons.includes(lessonId)) return;
        set({ viewedLessons: [...get().viewedLessons, lessonId] });
      },

      recordQuiz: (result) => {
        const prev = get().quizResults[result.quizId];
        set({
          quizResults: {
            ...get().quizResults,
            [result.quizId]: {
              ...result,
              // Sticky pass: a failed retake never revokes an earlier pass.
              passed: (prev?.passed ?? false) || (result.passed ?? false),
            },
          },
        });
      },

      removeQuiz: (quizId) => {
        const next = { ...get().quizResults };
        delete next[quizId];
        set({ quizResults: next });
      },

      reset: () => set({ viewedLessons: [], quizResults: {} }),
    }),
    { name: 'guest-progress' },
  ),
);
